import { useCallback, useEffect, useRef, useState } from "react";
import { useApp } from "@/contexts/AppContext";
import type {
  AnalyticsDateRange,
  AnalyticsFilters,
} from "@/features/analytics/types";
import { createDeviceListRequest } from "../utils";
import type { DeviceListResponse, DeviceListRow } from "../types";

type UseDeviceListParams = {
  selectedRange: AnalyticsDateRange;
  appliedFilters: AnalyticsFilters;
  page: number;
  pageSize: number;
};

type UseDeviceListResult = {
  rows: DeviceListRow[];
  isLoading: boolean;
  errorMessage: string;
  refresh: () => void;
};

export const useDeviceList = ({
  selectedRange,
  appliedFilters,
  page,
  pageSize,
}: UseDeviceListParams): UseDeviceListResult => {
  const { performRequest } = useApp();
  const [rows, setRows] = useState<DeviceListRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [refreshTick, setRefreshTick] = useState(0);
  const abortRef = useRef<AbortController | null>(null);

  const refresh = useCallback(() => {
    setRefreshTick((tick) => tick + 1);
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    abortRef.current?.abort();
    abortRef.current = controller;

    const payload = createDeviceListRequest(
      selectedRange,
      appliedFilters,
      page,
      pageSize,
    );

    setIsLoading(true);
    setErrorMessage("");

    performRequest<DeviceListResponse | null>("/devices", {
      method: "POST",
      body: JSON.stringify(payload),
      signal: controller.signal,
    })
      .then((response) => {
        if (controller.signal.aborted) return;
        if (response && Array.isArray(response.rows)) {
          setRows(response.rows);
        } else {
          setRows([]);
        }
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        if (error instanceof DOMException && error.name === "AbortError") return;
        setRows([]);
        setErrorMessage(
          error instanceof Error ? error.message : "Failed to load devices",
        );
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      });

    return () => {
      controller.abort();
    };
  }, [performRequest, selectedRange, appliedFilters, page, pageSize, refreshTick]);

  return { rows, isLoading, errorMessage, refresh };
};
